import type { ReactNode } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '../lib/cn';

type Tone = 'success' | 'danger' | 'warning' | 'info' | 'neutral' | 'primary';

const TONE: Record<Tone, string> = {
  success: 'bg-ops-success-soft text-ops-success',
  danger: 'bg-ops-danger-soft text-ops-danger',
  warning: 'bg-ops-warning-soft text-ops-warning',
  info: 'bg-ops-info-soft text-ops-info',
  primary: 'bg-ops-primary-soft text-ops-primary',
  neutral: 'bg-ops-surface-2 text-ops-text-secondary',
};

/** Бейдж статуса: tone = смысл (норма/на грани/проблема/инфо), опц. точка. */
export function StatusBadge({
  tone = 'neutral',
  children,
  dot = false,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  dot?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-ops-pill px-2.5 py-1 text-ops-caption font-semibold',
        TONE[tone],
        className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/** Дельта-бейдж: вектор «вырос/упал» — знак и цвет несут смысл. */
export function DeltaBadge({ value, unit = '', className }: { value: number; unit?: string; className?: string }) {
  const positive = value >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-ops-pill px-2 py-0.5 text-ops-caption font-semibold tabular-nums',
        positive ? 'bg-ops-success-soft text-ops-success' : 'bg-ops-danger-soft text-ops-danger',
        className,
      )}
    >
      <Icon size={13} strokeWidth={2.5} />
      {positive ? '+' : '−'}
      {Math.abs(value)}
      {unit}
    </span>
  );
}

/** Денежный бейдж: ₽ вклад / «деньги на столе», знак = цвет. */
export function MoneyBadge({ amount, currency = '₽', className }: { amount: number; currency?: string; className?: string }) {
  const positive = amount >= 0;
  const formatted = new Intl.NumberFormat('ru-RU').format(Math.abs(amount));
  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-0.5 font-semibold tabular-nums',
        positive ? 'text-ops-success' : 'text-ops-danger',
        className,
      )}
    >
      {positive ? '+' : '−'}
      {formatted}&nbsp;{currency}
    </span>
  );
}
